import { memo, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useMeasure } from 'react-use';

import {
  TableContainer,
  TableFooter,
  Pagination,
  TableV2,
  Tbody,
} from '@frontend/legacy-components';

import { PAGE_SIZE } from 'constants/all';

import {
  DefaultTableShadowOverlay,
  type DefaultTableShadowOverlayProps,
} from '../tableShadowArea';
import {
  SpinnerOverlay,
  EmptyContentWrapper,
  ScrollContainer,
  MemoizedDataRow,
  MemoizedTableHeader,
} from './components';
import type { ColumnConfig, RowDataConstraint, SortValue } from './types';

export type ListTableProps<
  ColumnId extends string,
  RowData extends RowDataConstraint,
> = {
  emphasizedDataRows: ReactNode;
  emphasizedDataIsEmpty: boolean;
  data: RowData[];
  isFetchingData?: boolean;
  columns: ColumnConfig<ColumnId, RowData>[];
  stickyColumnLeftId: ColumnId;
  currentSort: SortValue;
  onSort: (sortParam: string | null) => void;
  getRowLinkDestination: (rowData: RowData) => string;
  getIsRowHighlighted?: (rowData: RowData) => boolean;
  paginationCurrentPage: number;
  handlePageChange: (pageNumber: number) => void;
  paginationTotalItems: number;
  emptyContent: ReactNode;
};

export const ListTable = <
  ColumnId extends string,
  RowData extends RowDataConstraint,
>({
  emphasizedDataRows = null,
  emphasizedDataIsEmpty,
  data,
  isFetchingData = false,
  columns,
  stickyColumnLeftId,
  currentSort,
  onSort,
  getRowLinkDestination,
  getIsRowHighlighted,
  paginationCurrentPage,
  handlePageChange,
  paginationTotalItems,
  emptyContent,
}: ListTableProps<ColumnId, RowData>) => {
  const overflowBoxRef = useRef<HTMLDivElement>(null);
  const stickyTopLeftElementRef = useRef<HTMLTableCellElement>(null);

  const [footerRef, footerRect] = useMeasure();
  const footerHeight = footerRect.height;

  const stickyElementRefs = useMemo<
    DefaultTableShadowOverlayProps['stickyElementRefs']
  >(
    () => ({
      top: stickyTopLeftElementRef,
      left: stickyTopLeftElementRef,
    }),
    [stickyTopLeftElementRef],
  );

  const isEmpty = !(data.length > 0) && emphasizedDataIsEmpty;

  const numberOfColumns = columns.length;

  return (
    <TableContainer
      variant="simple"
      size="md"
      className="table-container"
      position="relative"
      display="flex"
      flex="1 1 100%"
      justifyContent="stretch"
      alignItems="stretch"
      overflow="hidden"
      borderRadius="0"
      borderBottomRadius={{ base: 0, tablet: 'lg' }}
      borderTop="hidden"
    >
      {isFetchingData && <SpinnerOverlay />}
      <DefaultTableShadowOverlay
        enableStickyBorders
        overflowBoxRef={overflowBoxRef}
        stickyElementRefs={stickyElementRefs}
        pb={`${footerHeight}px`}
      />
      <ScrollContainer ref={overflowBoxRef} tableIsEmpty={isEmpty}>
        <TableV2 tableLayout="fixed" height={isEmpty ? '100%' : undefined}>
          <MemoizedTableHeader
            columns={columns}
            currentSort={currentSort}
            onSort={onSort}
            stickyLeftColumnId={stickyColumnLeftId}
            stickyTopLeftElementRef={stickyTopLeftElementRef}
          />
          <Tbody zIndex={0}>
            {isEmpty ? (
              <EmptyContentWrapper numberOfColumns={numberOfColumns}>
                {emptyContent}
              </EmptyContentWrapper>
            ) : (
              <>
                {emphasizedDataRows}
                {data.map((rowData) => {
                  const rowLinkDestination = getRowLinkDestination(rowData);
                  const rowIsHighlighted =
                    getIsRowHighlighted?.(rowData) ?? false;

                  return (
                    <MemoizedDataRow
                      key={rowData.id}
                      columns={columns}
                      rowData={rowData}
                      stickyLeftColumnId={stickyColumnLeftId}
                      rowLinkDestination={rowLinkDestination}
                      rowIsHighlighted={rowIsHighlighted}
                    />
                  );
                })}
              </>
            )}
          </Tbody>
        </TableV2>
      </ScrollContainer>
      <TableFooter ref={footerRef} flex="0 0 auto" justifyContent="end">
        {!isEmpty && (
          <Pagination
            totalItems={paginationTotalItems}
            itemsPerPage={PAGE_SIZE}
            onChange={(value: number) => handlePageChange(value)}
            current={paginationCurrentPage}
          />
        )}
      </TableFooter>
    </TableContainer>
  );
};

export const MemoizedListTable = memo(ListTable);
